import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { useAuth } from './AuthContext'
import {
  LocationTracker,
  LocationTrackingState,
  LocationPoint,
  Geofence,
  DEFAULT_TRACKING_CONFIG,
  BATTERY_SAVING_CONFIG
} from '../services/location.service'
import { getSitesAsGeofences } from '../services/sites.service'
import { logger } from '../lib/logger'

/** Location tracking settings */
export interface LocationTrackingSettings {
  /** Enable location tracking during sessions */
  enabled: boolean
  /** Use battery saving mode */
  batterySaving: boolean
  /** Show geofence exit alerts */
  geofenceAlerts: boolean
}

/** Location context value */
interface LocationContextValue {
  locationTracking: LocationTrackingState
  locationSettings: LocationTrackingSettings
  updateLocationSettings: (settings: Partial<LocationTrackingSettings>) => void
  startLocationTracking: (sessionId: string) => Promise<void>
  stopLocationTracking: () => void
  lastKnownLocation: LocationPoint | null
  isInsideGeofence: boolean
  onGeofenceExit: ((geofence: Geofence) => void) | null
  setOnGeofenceExit: (callback: ((geofence: Geofence) => void) | null) => void
  geofences: Geofence[]
}

/** Props for LocationProvider */
interface LocationProviderProps {
  children: ReactNode
}

const LocationContext = createContext<LocationContextValue | null>(null)

// Default location settings
const DEFAULT_LOCATION_SETTINGS: LocationTrackingSettings = {
  enabled: true,
  batterySaving: false,
  geofenceAlerts: true
}

// Default location tracking state
const DEFAULT_LOCATION_STATE: LocationTrackingState = {
  isTracking: false,
  lastLocation: null,
  error: null,
  isBackground: false
}

export function LocationProvider({ children }: LocationProviderProps): React.ReactElement {
  const { user, isApproved } = useAuth()

  // Location tracking state
  const [locationSettings, setLocationSettings] = useState<LocationTrackingSettings>(DEFAULT_LOCATION_SETTINGS)
  const [locationTracking, setLocationTracking] = useState<LocationTrackingState>(DEFAULT_LOCATION_STATE)
  const [lastKnownLocation, setLastKnownLocation] = useState<LocationPoint | null>(null)
  const [isInsideGeofence, setIsInsideGeofence] = useState(true)
  const [geofences, setGeofences] = useState<Geofence[]>([])
  const [onGeofenceExitCallback, setOnGeofenceExitCallback] = useState<((geofence: Geofence) => void) | null>(null)

  // Location tracker ref
  const trackerRef = useRef<LocationTracker | null>(null)

  // Load geofences when user is authenticated
  useEffect(() => {
    if (!user || !isApproved) {
      setGeofences([])
      return
    }

    async function loadGeofences(): Promise<void> {
      try {
        const siteGeofences = await getSitesAsGeofences()
        setGeofences(siteGeofences)
      } catch (err) {
        logger.error('Failed to load geofences', err as Error)
      }
    }

    loadGeofences()
  }, [user, isApproved])

  // Initialize location tracker
  useEffect(() => {
    const config = locationSettings.batterySaving
      ? BATTERY_SAVING_CONFIG
      : DEFAULT_TRACKING_CONFIG

    trackerRef.current = new LocationTracker(config)

    return () => {
      if (trackerRef.current) {
        trackerRef.current.destroy()
        trackerRef.current = null
      }
    }
  }, [locationSettings.batterySaving])

  // Start location tracking function - now takes sessionId as parameter
  const startLocationTracking = useCallback(async (sessionId: string): Promise<void> => {
    if (!locationSettings.enabled || !trackerRef.current) {
      return
    }

    await trackerRef.current.start(sessionId, {
      geofences,
      onGeofenceExit: (geofence) => {
        setIsInsideGeofence(false)
        if (locationSettings.geofenceAlerts && onGeofenceExitCallback) {
          onGeofenceExitCallback(geofence)
        }
        logger.warn('Worker exited geofence', { geofence: geofence.name })
      },
      onLocationUpdate: (location) => {
        setLastKnownLocation(location)
      },
      onStateChange: (state) => {
        setLocationTracking(state)
        if (state.lastLocation) {
          setLastKnownLocation(state.lastLocation)
        }
      }
    })
  }, [locationSettings.enabled, locationSettings.geofenceAlerts, geofences, onGeofenceExitCallback])

  // Stop location tracking function
  const stopLocationTracking = useCallback((): void => {
    if (trackerRef.current) {
      trackerRef.current.stop()
    }
    setLocationTracking(DEFAULT_LOCATION_STATE)
    setIsInsideGeofence(true)
  }, [])

  // Update location settings
  const updateLocationSettings = useCallback((settings: Partial<LocationTrackingSettings>): void => {
    setLocationSettings(prev => ({ ...prev, ...settings }))
  }, [])

  // Set geofence exit callback
  const setOnGeofenceExit = useCallback((callback: ((geofence: Geofence) => void) | null): void => {
    setOnGeofenceExitCallback(() => callback)
  }, [])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<LocationContextValue>(() => ({
    locationTracking,
    locationSettings,
    updateLocationSettings,
    startLocationTracking,
    stopLocationTracking,
    lastKnownLocation,
    isInsideGeofence,
    onGeofenceExit: onGeofenceExitCallback,
    setOnGeofenceExit,
    geofences,
  }), [
    locationTracking,
    locationSettings,
    updateLocationSettings,
    startLocationTracking,
    stopLocationTracking,
    lastKnownLocation,
    isInsideGeofence,
    onGeofenceExitCallback,
    setOnGeofenceExit,
    geofences,
  ])

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  )
}

export function useLocation(): LocationContextValue {
  const context = useContext(LocationContext)
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider')
  }
  return context
}

export default LocationContext
